import React, { useState } from 'react';
import { MdExpandLess, MdExpandMore, MdRefresh } from 'react-icons/md';

// Shared primitives for Project Delta. Mobile-first: every component below is sized for
// 375px and scales up, and anything that can overflow scrolls inside its own container
// so the page body never scrolls sideways.
//
// Colour here is SEMANTIC, not sign-based: these are magnitudes (a carrying value is
// never negative), so the house green/red rule applies only to figures that can actually
// go both ways — Distributable Liquidity is the one that can, and `Tile` colours it.
// Everywhere else green means unencumbered, red means pledged, amber means partly
// pledged. That mapping is fixed below so it can never drift between screens.

export const AVAIL = {
  free: { bar: '#2F6B4F', text: 'text-emerald-700 dark:text-emerald-400', chip: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' },
  partial: { bar: '#C9A227', text: 'text-amber-700 dark:text-amber-400', chip: 'bg-amber-500/10 text-amber-700 dark:text-amber-400' },
  pledged: { bar: '#9B2C2C', text: 'text-rose-700 dark:text-rose-400', chip: 'bg-rose-500/10 text-rose-700 dark:text-rose-400' },
};

export const LIQ = {
  liquid: { bar: '#2E4E86', chip: 'bg-blue-500/10 text-blue-700 dark:text-blue-400' },
  semi: { bar: '#8C6D3F', chip: 'bg-amber-600/10 text-amber-800 dark:text-amber-500' },
  illiquid: { bar: '#8A8A80', chip: 'bg-gray-500/10 text-gray-600 dark:text-gray-400' },
};

export const Label = ({ children, className = '' }) => (
  <div className={`text-[9px] uppercase tracking-[2px] text-gray-400 dark:text-gray-500 ${className}`}>
    {children}
  </div>
);

// A headline figure. `tone` picks a left accent; `signed` turns on the house green/red
// rule for values that can genuinely be negative.
export const Tile = ({ label, value, sub, tone, signed = false, numeric }) => {
  const accent = tone === 'warn' ? 'border-l-2 border-rose-500'
    : tone === 'ok' ? 'border-l-2 border-emerald-500'
    : 'border-l-2 border-transparent';
  const signCls = signed && typeof numeric === 'number'
    ? (numeric < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400')
    : 'text-gray-900 dark:text-gray-100';
  return (
    <div className={`rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-[#0d1117] sm:p-4 ${accent}`}>
      <Label>{label}</Label>
      <div className={`mt-1.5 font-serif text-xl leading-none tracking-tight sm:text-2xl ${signCls}`}>
        {value}
      </div>
      {sub && <div className="mt-1.5 text-[11px] leading-snug text-gray-500 dark:text-gray-400">{sub}</div>}
    </div>
  );
};

// Collapsible section — the house pattern for every data block, with an optional rerun
// button that bypasses the client cache.
export const Section = ({ title, subtitle, children, defaultOpen = true, onRerun, busy }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0d1117]">
      <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2.5 dark:border-gray-800 sm:px-4">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={open}
        >
          <span className="shrink-0 text-gray-400">{open ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}</span>
          <span className="min-w-0">
            <span className="block truncate font-serif text-sm text-gray-900 dark:text-gray-100">{title}</span>
            {subtitle && <span className="block truncate text-[11px] text-gray-500 dark:text-gray-400">{subtitle}</span>}
          </span>
        </button>
        {onRerun && (
          <button
            onClick={onRerun}
            disabled={busy}
            title="Refresh, bypassing the cache"
            className="shrink-0 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-brand-gold disabled:opacity-40 dark:hover:bg-gray-800"
          >
            <MdRefresh size={16} className={busy ? 'animate-spin' : ''} />
          </button>
        )}
      </div>
      {open && <div className="p-3 sm:p-4">{children}</div>}
    </section>
  );
};

// Horizontally scrollable filter pills. The house mobile pattern — compact pills that
// scroll rather than a dropdown grid that wraps and pushes content off-screen.
export const PillGroup = ({ label, options, value, onChange }) => (
  <div className="min-w-0">
    {label && <Label className="mb-1.5">{label}</Label>}
    <div className="scrollbar-thin -mx-0.5 flex gap-1.5 overflow-x-auto pb-1">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value ?? 'all'}
            onClick={() => onChange(o.value)}
            aria-pressed={active}
            className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
              active
                ? 'border-brand-gold bg-brand-gold/10 font-medium text-brand-gold'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

export const Chip = ({ children, className = '' }) => (
  <span className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ${className}`}>
    {children}
  </span>
);

// Stacked proportion bar. Segments are amounts, matching the server's aggregation.
export const StackedBar = ({ parts, total }) => (
  <div className="flex h-5 overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
    {parts.filter((p) => p.value > 0).map((p) => (
      <div
        key={p.key}
        title={`${p.label}`}
        style={{ width: `${total ? (p.value / total) * 100 : 0}%`, background: p.color }}
      />
    ))}
  </div>
);

// The provisional marker. The brief is explicit that this stays visible until the register
// is signed off in full, and that the caveat is never hidden in a tooltip.
//
// WRITTEN IN PLAIN WORDS ON PURPOSE. This previously read "the register is 33% signed off
// by count and 12% by value", which is precise and means nothing to anyone who has not
// been told what the two denominators are. The reader of this line is a Shareholder, not
// an operator. It now says how many assets have been checked, and how much of the money
// those cover — which is the same fact, and carries its own explanation.
//
// Both numbers stay, because the gap between them is the informative part: 8 of 10 assets
// checked covering 15% of the value means the large positions are the ones outstanding,
// and every total on the screen is close to meaningless.
export const ProvisionalNote = ({ completeness }) => {
  if (!completeness || completeness.is_complete) return null;
  const { signed_off_count: done, total_count: total, by_value: byValue } = completeness;
  const remaining = (total || 0) - (done || 0);
  const share = byValue === null || byValue === undefined ? null : `${(byValue * 100).toFixed(0)}%`;
  return (
    <div className="mb-4 rounded-lg border-l-2 border-amber-500 bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-900 dark:bg-amber-500/10 dark:text-amber-200 sm:px-4">
      <span className="font-semibold">These figures are provisional.</span>{' '}
      {total
        ? <>
            {done} of {total} assets have been checked and signed off
            {share ? <>, covering {share} of the book&rsquo;s total value</> : null}.
            The figures below will move as the remaining {remaining} {remaining === 1 ? 'is' : 'are'} checked.
          </>
        : <>No assets have been entered on the register yet, so there is nothing behind these figures.</>}
    </div>
  );
};

export const Empty = ({ children }) => (
  <div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">{children}</div>
);
